/**
 * ForgeRock / PingOne Advanced Identity Cloud as an OpenID Connect provider for MJExplorer
 * (Task 28; spec D15/D18/D19 in plans/research-accelerator/2026-09-02-member-portal-spec.md).
 *
 * Registered via `@RegisterClass(MJAuthBase, 'forgerock')` — this only makes the class available
 * to `AngularAuthProviderFactory`/`AuthServicesModule.forRoot`'s `ClassFactory.GetRegistration`
 * lookup (node_modules/@memberjunction/ng-auth-services/dist/lib/auth-services.module.js).
 * `forRoot` instantiates it when `environment.AUTH_TYPE === 'forgerock'` — the live login path on
 * stage and in dev since 2026-09-22 (prod pending). The dormant branch below only applies to an
 * environment that has not been switched over (AUTH_TYPE still pointing elsewhere, or the
 * FORGEROCK_* keys below still empty).
 *
 * Implements the v3.0.0 `MJAuthBase` abstract contract the same way the installed Auth0 and Okta
 * providers do (node_modules/@memberjunction/ng-auth-services/dist/lib/providers/mjexplorer-{auth0,okta}-provider.service.js)
 * — `initialize()` does silent session restore, `loginInternal()`/`logoutInternal()` redirect the
 * browser, and the `extract*Internal()` methods hide this provider's token storage shape from the
 * rest of Explorer, mirroring their structure per the brief's Step 1.
 *
 * Uses `oauth4webapi` directly (no ForgeRock-specific Angular SDK exists, unlike Auth0/Okta) for
 * discovery, PKCE, and the Authorization Code Grant. `oauth4webapi` is deliberately low-level —
 * see forgerock-oidc.ts for the pure helpers (authorization URL construction, claim mapping) this
 * class composes into the stateful flow below.
 */
import { Inject, Injectable, InjectionToken, Provider } from '@angular/core';
import { RegisterClass } from '@memberjunction/global';
import {
  AngularAuthProviderConfig,
  AuthErrorType,
  MJAuthBase,
  StandardAuthError,
  StandardAuthToken,
  StandardUserInfo,
  TokenRefreshResult,
} from '@memberjunction/ng-auth-services';
import * as oauth from 'oauth4webapi';
import {
  BuildForgeRockAuthorizationUrl,
  DecodeUnverifiedJwtPayload,
  ExtractForgeRockClaims,
  ForgeRockEnvironmentConfig,
  IsForgeRockConfigured,
  IsLocalInsecureIssuer,
  IsSafeReturnPath,
  MapForgeRockClaimsToStandardUserInfo,
  PkceAttempt,
  ResolveReturnTo,
  UrlHasAuthCallbackParams,
} from './forgerock-oidc';
import { ClassifyMJCacheIdentityError, DeleteIndexedDatabase, ReconcileMJCacheIdentity } from './mj-cache-identity';

export const FORGEROCK_CONFIG = new InjectionToken<ForgeRockEnvironmentConfig>('FORGEROCK_CONFIG');

// sessionStorage (not localStorage): scoped to one tab/session, cleared automatically when the
// tab closes, and never touched by `MJAuthBase.clearClientCaches()`'s localStorage sweep on
// logout — this provider clears its own keys in `logoutInternal()` instead.
const STORAGE_PREFIX = 'mj-forgerock-';
const STATE_KEY = `${STORAGE_PREFIX}state`;
const NONCE_KEY = `${STORAGE_PREFIX}nonce`;
const VERIFIER_KEY = `${STORAGE_PREFIX}code-verifier`;
const ID_TOKEN_KEY = `${STORAGE_PREFIX}id-token`;
const REFRESH_TOKEN_KEY = `${STORAGE_PREFIX}refresh-token`;
const EXPIRES_AT_KEY = `${STORAGE_PREFIX}expires-at`;
const RETURN_TO_KEY = `${STORAGE_PREFIX}return-to`;

function readForgeRockEnvironmentConfig(environment: Record<string, unknown>): ForgeRockEnvironmentConfig {
  const issuer = environment['FORGEROCK_ISSUER'];
  const clientID = environment['FORGEROCK_CLIENTID'];
  const redirectUri = environment['FORGEROCK_REDIRECT_URI'];
  const scopes = environment['FORGEROCK_SCOPES'];
  const service = environment['FORGEROCK_SERVICE'];
  return {
    FORGEROCK_ISSUER: typeof issuer === 'string' ? issuer : '',
    FORGEROCK_CLIENTID: typeof clientID === 'string' ? clientID : '',
    // Default is the origin itself (not `${origin}/callback`): the Explorer shell
    // (node_modules/@memberjunction/ng-explorer-app/dist/lib/explorer-app.component.js) captures
    // `initialPath` in its constructor and later `navigateByUrl(initialPath)`s to it, but the
    // explorer route table has no `''`/`**` route — a callback landing on any path other than the
    // origin `navigateByUrl`s into a route that doesn't exist and the user lands in an empty
    // shell. `RedirectComponent` is bootstrapped at any path, so `/callback` buys nothing; this
    // mirrors exactly what the Auth0 provider defaults to. The switch-over runbook (Task 29) sets
    // `FORGEROCK_REDIRECT_URI` to the deployed origin.
    FORGEROCK_REDIRECT_URI: typeof redirectUri === 'string' && redirectUri.length > 0 ? redirectUri : window.location.origin,
    FORGEROCK_SCOPES: typeof scopes === 'string' && scopes.length > 0 ? scopes : 'openid profile email',
    FORGEROCK_SERVICE: typeof service === 'string' ? service : '',
  };
}

/**
 * ForgeRock authentication provider implementation - v3.0.0 contract.
 *
 * Implements the abstract methods from MJAuthBase to hide ForgeRock/oauth4webapi-specific
 * details. The key abstraction is that this provider stores the raw ID token string it obtains
 * from the token endpoint; consumers never need to know about oauth4webapi's `TokenEndpointResponse`
 * shape.
 */
@Injectable({ providedIn: 'root' })
@RegisterClass(MJAuthBase, 'forgerock')
export class MJForgeRockProvider extends MJAuthBase {
  static readonly PROVIDER_TYPE = 'forgerock';
  readonly type = MJForgeRockProvider.PROVIDER_TYPE;

  private readonly client: oauth.Client;
  private authServer: oauth.AuthorizationServer | null = null;
  private idToken: string | null = null;
  private refreshTokenValue: string | null = null;
  private tokenExpiresAt = 0;

  /**
   * Factory function to provide Angular dependencies required by ForgeRock. Stored as a static
   * property so `AngularAuthProviderFactory.getProviderAngularServices` can read it without
   * instantiating the provider (same pattern as the Auth0/Okta providers' `angularProviderFactory`).
   */
  static angularProviderFactory = (environment: Record<string, unknown>): Provider[] => [
    {
      provide: FORGEROCK_CONFIG,
      useValue: readForgeRockEnvironmentConfig(environment),
    },
  ];

  constructor(@Inject(FORGEROCK_CONFIG) private readonly forgerockConfig: ForgeRockEnvironmentConfig) {
    const config: AngularAuthProviderConfig = {
      name: MJForgeRockProvider.PROVIDER_TYPE,
      type: MJForgeRockProvider.PROVIDER_TYPE,
    };
    super(config);
    this.client = { client_id: this.forgerockConfig.FORGEROCK_CLIENTID };
  }

  // ============================================================================
  // LIFECYCLE METHODS
  // ============================================================================

  async initialize(): Promise<void> {
    await this.initializeSession();
    if (!this.lastAuthState) return;
    await this.reconcileCacheIdentity();
  }

  /**
   * MJ client caches must belong to the user this session starts for (upstream defect #15; see
   * mj-cache-identity.ts). The magic-link provider (AGUMagicLinkProvider) reconciles the same
   * marker, so an SSO sign-in after a magic-link guest — or a magic link after this SSO session —
   * clears the other identity's snapshot before Explorer's setup reads it. Runs inside
   * initialize() (APP_INITIALIZER), so it must not throw: a failed clear fails closed instead —
   * not authenticated, the login screen shows MJ_CACHE_IDENTITY_USER_MESSAGE, and its button signs
   * out (loginInternal).
   */
  private async reconcileCacheIdentity(): Promise<void> {
    try {
      await ReconcileMJCacheIdentity(this.lastUserEmail, {
        Storage: localStorage,
        ClearClientCaches: () => this.clearClientCaches(),
        DeleteDatabase: (name) => DeleteIndexedDatabase(name),
      });
    } catch (error) {
      console.error('[ForgeRock] Not starting the session: the previous sign-in\'s MJ caches could not be cleared.', error);
      this.cacheIdentityFailed = true;
      this.updateAuthState(false);
      this.userInfo$.error(error);
    }
  }

  private cacheIdentityFailed = false;
  private lastAuthState = false;
  private lastUserEmail: string | null = null;

  protected override updateAuthState(isAuthenticated: boolean): void {
    this.lastAuthState = isAuthenticated;
    super.updateAuthState(isAuthenticated);
  }

  protected override updateUserInfo(userInfo: StandardUserInfo | null): void {
    this.lastUserEmail = userInfo?.email ?? null;
    super.updateUserInfo(userInfo);
  }

  private async initializeSession(): Promise<void> {
    if (!IsForgeRockConfigured(this.forgerockConfig)) {
      // Dormant: registered but not configured. This provider is selected when
      // `environment.AUTH_TYPE === 'forgerock'` (stage/dev since 2026-09-22, prod pending), where
      // the FORGEROCK_* keys are always set; this guard only matters for an environment that has
      // not been switched over, or for something reaching the class directly (e.g. a test harness).
      console.warn('[ForgeRock] Provider registered but FORGEROCK_* keys are empty — dormant.');
      this.updateAuthState(false);
      return;
    }

    // `initialize()` is Angular's APP_INITIALIZER (apps/MJExplorer/src/app/app.module.ts): a
    // rejection here aborts the whole bootstrap — blank page, no Log In button. Nothing below may
    // throw; every failure degrades to "not authenticated" and the login screen.
    // Captured before anything strips the URL: `handleCallback()`'s `finally` rewrites
    // `window.location` via replaceState, so `isForgeRockCallbackUrl()` can no longer answer this
    // question after the block below runs — and the pre-routing capture must not fire on a
    // callback load (see rememberReturnPathBeforeRouting).
    const handlingCallback = this.isForgeRockCallbackUrl();
    if (handlingCallback) {
      // First login after the IdP redirect lands here with `?code=…` (or `?error=…`) still in the
      // URL and no token in sessionStorage yet. The Explorer shell reads auth state exactly once
      // right after APP_INITIALIZER (`getUserInfo().pipe(take(1))`), so the exchange must finish
      // here — exactly as the installed Okta provider awaits `handleRedirect()` inside its own
      // initialize(). Without this the shell reads "not authenticated", stalls on
      // "Loading workspace…", and only a manual reload recovers. `handleCallback()` persists the
      // token, updates user info, sets auth state, and strips the callback params off the URL.
      try {
        await this.handleCallback();
        // Success: the exchange already set auth state and user info from the fresh token, so the
        // stored-session restore below has nothing to add and must not run.
        return;
      } catch (error) {
        // IdP `?error=` (user cancelled at AGU), a stale `?code=` whose PKCE keys are gone, or a
        // token-endpoint/network failure. handleCallback()'s `finally` already stripped the URL
        // and cleared the pending keys; show the login screen — *unless* this tab still holds a
        // valid token (e.g. Back into an AGU journey page re-issues a code into a tab that is
        // already logged in), which is why this falls through to the stored-session restore below
        // instead of returning. Restoring a still-valid session is always correct here.
        console.error('[ForgeRock] Login callback failed; showing the login screen.', error);
        this.updateAuthState(false);
        this.updateUserInfo(null);
      }
    }

    // Silent session restore, mirroring the Auth0/Okta providers' initialize(): if a still-valid
    // ID token is cached from an earlier page load in this tab, restore auth state from it
    // without a redirect. An expired one is removed so nothing can re-read it. Deliberately no
    // `ensureDiscovery()` pre-warm on this path: restore only reads sessionStorage and
    // base64-decodes the cached JWT, so it needs no network, and a transient discovery outage
    // must never drop an already-authenticated user back to the login screen. `loginInternal()`
    // and `doHandleCallback()` each call `ensureDiscovery()` themselves when they actually need
    // the authorization server, so nothing is lost by not warming it here. `sessionStorage` access
    // can itself throw (e.g. `SecurityError` when the browser blocks site data for the origin), so
    // this whole block is guarded too — nothing below may throw out of `initialize()`.
    let sessionRestored = false;
    try {
      const storedIdToken = sessionStorage.getItem(ID_TOKEN_KEY);
      const storedExpiresAt = sessionStorage.getItem(EXPIRES_AT_KEY);
      if (storedIdToken && storedExpiresAt && Number(storedExpiresAt) > Date.now()) {
        this.idToken = storedIdToken;
        this.refreshTokenValue = sessionStorage.getItem(REFRESH_TOKEN_KEY);
        this.tokenExpiresAt = Number(storedExpiresAt);
        if (this.restoreUserInfoFromIdToken(storedIdToken)) {
          this.updateAuthState(true);
          sessionRestored = true;
        } else {
          // The cached value is not a decodable ForgeRock ID token (only reachable by hand-editing
          // sessionStorage — persistTokens() only ever stores validated tokens). Authenticating
          // with a null user is the exact shape of the original "Loading workspace…" hang: the
          // shell's single `getUserInfo().pipe(take(1))` emits null while the template renders the
          // authenticated branch. Drop the session and show the login screen instead.
          this.clearSession();
          this.updateAuthState(false);
        }
      } else if (storedIdToken || storedExpiresAt) {
        this.clearSession();
      }
      // Runs after `clearSession()` (which removes RETURN_TO_KEY) so the capture is never undone.
      if (!sessionRestored && !handlingCallback) {
        this.rememberReturnPathBeforeRouting();
      }
    } catch (error) {
      console.error('[ForgeRock] Stored-session restore failed; showing the login screen.', error);
      this.updateAuthState(false);
      this.updateUserInfo(null);
    }
  }

  /**
   * The Explorer shell's AuthGuard returns `false` for an unauthenticated route
   * (`@memberjunction/ng-explorer-core/dist/lib/guards/auth-guard.service.js` `canActivate()`), so
   * Angular cancels the initial navigation and the URL is already `/` by the time the login screen
   * renders — the click-time capture in loginInternal() then sees nothing to return to (stage
   * check b, 2026-09-23). This runs from initialize() (APP_INITIALIZER, before the router), on the
   * not-authenticated path only, and only overwrites the key with a path IsSafeReturnPath()
   * accepts — a reload of the login screen at `/` keeps the earlier capture.
   */
  private rememberReturnPathBeforeRouting(): void {
    try {
      const candidate = window.location.pathname + window.location.search;
      if (IsSafeReturnPath(candidate)) {
        sessionStorage.setItem(RETURN_TO_KEY, candidate);
      }
    } catch (error) {
      console.error('[ForgeRock] Could not record the pre-login path.', error);
    }
  }

  protected async loginInternal(options?: Record<string, unknown>): Promise<void> {
    if (this.cacheIdentityFailed) {
      // Fail-closed state (reconcileCacheIdentity): the login screen's button signs out instead.
      await this.logout();
      return;
    }
    if (!IsForgeRockConfigured(this.forgerockConfig)) {
      throw new Error('ForgeRock login attempted while dormant (FORGEROCK_* keys are empty)');
    }
    const as = await this.ensureDiscovery();
    const pkce = await this.generatePkceAttempt();
    sessionStorage.setItem(STATE_KEY, pkce.state);
    sessionStorage.setItem(NONCE_KEY, pkce.nonce);
    sessionStorage.setItem(VERIFIER_KEY, pkce.codeVerifier);
    // Return-to (spec H4): MJ's `login({ appState: { target } })` convention first (the shell's
    // handleAuthRetry uses it), then the page this login started from, and finally the path
    // `rememberReturnPathBeforeRouting()` captured in initialize(). That last fallback is what
    // carries an unauthenticated deep link: the shell's AuthGuard has already collapsed the URL to
    // `/` by the time the user clicks Log in, so the click-time path is `/` and resolves to null.
    // This is also the path the 60-minute redirect re-auth takes — handleSessionExpiryInternal() →
    // loginInternal() — so a user who was on /app/maps when the session ended comes back to
    // /app/maps, not Home. The stored value is re-validated, never trusted on sight.
    const current = window.location.pathname + window.location.search;
    const stored = sessionStorage.getItem(RETURN_TO_KEY);
    const returnTo = ResolveReturnTo(options, current) ?? (stored !== null && IsSafeReturnPath(stored) ? stored : null);
    if (returnTo !== null) {
      sessionStorage.setItem(RETURN_TO_KEY, returnTo);
    } else {
      sessionStorage.removeItem(RETURN_TO_KEY);
    }
    const url = BuildForgeRockAuthorizationUrl(
      as,
      this.forgerockConfig.FORGEROCK_CLIENTID,
      this.forgerockConfig.FORGEROCK_REDIRECT_URI,
      this.forgerockConfig.FORGEROCK_SCOPES,
      pkce,
      this.forgerockConfig.FORGEROCK_SERVICE
    );
    window.location.assign(url.toString());
  }

  protected async logoutInternal(): Promise<void> {
    const idTokenHint = this.idToken;
    this.clearSession();
    this.updateAuthState(false);
    this.updateUserInfo(null);

    if (!IsForgeRockConfigured(this.forgerockConfig)) {
      return;
    }
    // Local state is already cleared above. The RP-initiated logout leg below reaches out to the
    // IdP for discovery and its end_session_endpoint — if the IdP is unreachable, that must never
    // turn a local logout into a rejected promise (the user would appear stuck "logged in" in the
    // UI even though session state is already gone). Log and fall through instead of throwing.
    try {
      const as = await this.ensureDiscovery();
      if (as.end_session_endpoint) {
        // RP-initiated logout (OpenID Connect RP-Initiated Logout 1.0) when ForgeRock advertises it.
        const url = new URL(as.end_session_endpoint);
        if (idTokenHint) {
          url.searchParams.set('id_token_hint', idTokenHint);
        }
        url.searchParams.set('post_logout_redirect_uri', window.location.origin);
        window.location.assign(url.toString());
      }
    } catch (error) {
      console.warn('[ForgeRock] RP-initiated logout failed (IdP unreachable or discovery failed); local session was already cleared.', error);
    }
  }

  /**
   * Handles the OAuth callback. Reused by the generic `app-redirect` (`RedirectComponent`) the
   * same way Auth0/Okta are — see node_modules/@memberjunction/ng-auth-services/dist/lib/redirect.component.js,
   * which calls `authService.handleCallback()` whenever the current URL carries `code=`/`state=`.
   */
  async handleCallback(): Promise<void> {
    // Both `initialize()` (on the redirect load) and the generic `RedirectComponent` invoke this
    // for the same callback URL, and the authorization code is single-use — so memoize the
    // in-flight exchange and let every caller await the one attempt. A genuine second login is a
    // full page navigation that re-instantiates this provider, resetting the memo.
    this.callbackPromise ??= this.doHandleCallback();
    return this.callbackPromise;
  }

  private callbackPromise: Promise<void> | null = null;

  private async doHandleCallback(): Promise<void> {
    if (!IsForgeRockConfigured(this.forgerockConfig)) {
      return;
    }
    // Same gate as initialize(): path + params, so the generic `RedirectComponent` (which calls
    // handleCallback() on any URL carrying `code=`/`state=`) cannot make this provider consume
    // another route's OAuth callback — notably MJExplorer's `/oauth/callback` MCP route.
    if (!this.isForgeRockCallbackUrl()) {
      return;
    }
    const currentUrl = new URL(window.location.href);

    try {
      const expectedState = sessionStorage.getItem(STATE_KEY);
      const expectedNonce = sessionStorage.getItem(NONCE_KEY);
      const codeVerifier = sessionStorage.getItem(VERIFIER_KEY);
      if (!expectedState || !expectedNonce || !codeVerifier) {
        // Guarded inside the try (not before it) so the `finally` cleanup below — clearing the
        // sessionStorage keys and stripping `?code=...` off the URL via replaceState — always
        // runs, even for a callback URL that gets reloaded/re-hit after the pending attempt's
        // keys are already gone. Left outside the try, this throw would skip that cleanup and
        // leave the stale `code=`/`state=` query string on the URL indefinitely.
        throw new Error('ForgeRock callback received with no pending authorization attempt recorded in sessionStorage');
      }

      const as = await this.ensureDiscovery();
      const params = oauth.validateAuthResponse(as, this.client, currentUrl, expectedState);
      const response = await oauth.authorizationCodeGrantRequest(
        as,
        this.client,
        oauth.None(),
        params,
        this.forgerockConfig.FORGEROCK_REDIRECT_URI,
        codeVerifier,
        this.insecureRequestOptions()
      );
      const result = await oauth.processAuthorizationCodeResponse(as, this.client, response, {
        expectedNonce,
        requireIdToken: true,
      });
      const idTokenClaims = oauth.getValidatedIdTokenClaims(result);
      if (!result.id_token || !idTokenClaims) {
        throw new Error('ForgeRock token response did not include a validated ID token');
      }

      this.persistTokens(result.id_token, result.refresh_token ?? null, idTokenClaims.exp * 1000);
      const claims = ExtractForgeRockClaims(idTokenClaims);
      this.updateUserInfo(MapForgeRockClaimsToStandardUserInfo(claims));
      this.updateAuthState(true);
    } finally {
      sessionStorage.removeItem(STATE_KEY);
      sessionStorage.removeItem(NONCE_KEY);
      sessionStorage.removeItem(VERIFIER_KEY);
      // Strip the callback params — and land on the page the login started from. The Explorer
      // shell captures `initialPath` from window.location *after* APP_INITIALIZER (which is where
      // this runs), so restoring it here is what makes a deep link / the 60-minute re-auth return
      // the user to their page. A path is only ever stored if IsSafeReturnPath() accepted it.
      const returnTo = sessionStorage.getItem(RETURN_TO_KEY);
      sessionStorage.removeItem(RETURN_TO_KEY);
      try {
        window.history.replaceState({}, document.title, returnTo ?? window.location.pathname);
      } catch (error) {
        // This runs in a `finally`: a throw here would mask the original callback error and, worse,
        // demote an already-successful login (updateAuthState(true) above) to the login screen via
        // initialize()'s catch. Leaving the URL as-is is always the safer outcome.
        console.error('[ForgeRock] Could not restore the return path; leaving the URL as-is.', error);
      }
    }
  }

  // ============================================================================
  // ABSTRACT METHOD IMPLEMENTATIONS (v3.0.0)
  // ============================================================================

  protected async extractIdTokenInternal(): Promise<string | null> {
    return this.idToken;
  }

  protected async extractTokenInfoInternal(): Promise<StandardAuthToken | null> {
    if (!this.idToken) {
      return null;
    }
    return {
      idToken: this.idToken,
      expiresAt: this.tokenExpiresAt,
      scopes: this.forgerockConfig.FORGEROCK_SCOPES.split(' ').filter((s) => s.length > 0),
    };
  }

  protected async extractUserInfoInternal(): Promise<StandardUserInfo | null> {
    if (!this.idToken) {
      return null;
    }
    const claims = ExtractForgeRockClaims(DecodeUnverifiedJwtPayload(this.idToken));
    return MapForgeRockClaimsToStandardUserInfo(claims);
  }

  /**
   * The primary 60-minute path. AGU's tenant issues a refresh token on the authorization-code
   * grant even though discovery does not advertise `offline_access` and this provider does not
   * request it — verified live on stage 2026-09-23 (a 7-day JWT with `tokenName: refresh_token`,
   * scope `openid profile email`; write-up
   * docs/ResearchAccelerator/testing_results/2026-09-23-forgerock-login-hardening-stage.md, F1).
   * So an expired ID token is normally replaced by the refresh grant below, in place, with no
   * navigation: the user stays on the page they were on.
   *
   * The fallback is the redirect re-auth: when no usable refresh token exists (absent, expired
   * after 7 days, or revoked at the IdP) this returns `TOKEN_EXPIRED`, so `MJAuthBase.refreshToken()`
   * invokes `handleSessionExpiryInternal()` and a fresh interactive login is triggered.
   */
  protected async refreshTokenInternal(): Promise<TokenRefreshResult> {
    if (!this.refreshTokenValue) {
      return {
        success: false,
        error: {
          type: AuthErrorType.TOKEN_EXPIRED,
          message: 'No refresh token available',
          userMessage: 'Your session has expired. Please log in again.',
        },
      };
    }
    try {
      const as = await this.ensureDiscovery();
      const response = await oauth.refreshTokenGrantRequest(as, this.client, oauth.None(), this.refreshTokenValue, this.insecureRequestOptions());
      const result = await oauth.processRefreshTokenResponse(as, this.client, response);
      const idTokenClaims = oauth.getValidatedIdTokenClaims(result);
      if (!result.id_token || !idTokenClaims) {
        return {
          success: false,
          error: {
            type: AuthErrorType.TOKEN_EXPIRED,
            message: 'Refresh response did not include a validated ID token',
            userMessage: 'Session refresh failed. Please log in again.',
          },
        };
      }
      this.persistTokens(result.id_token, result.refresh_token ?? this.refreshTokenValue, idTokenClaims.exp * 1000);
      return {
        success: true,
        token: {
          idToken: result.id_token,
          accessToken: result.access_token,
          expiresAt: idTokenClaims.exp * 1000,
          scopes: result.scope ? result.scope.split(' ') : undefined,
        },
      };
    } catch (error) {
      return { success: false, error: this.classifyErrorInternal(error) };
    }
  }

  /**
   * Classifies `oauth4webapi`'s typed errors into `AuthErrorType`, mirroring how the Okta
   * provider maps its SDK's error shapes (node_modules/@memberjunction/ng-auth-services/dist/lib/providers/mjexplorer-okta-provider.service.js).
   */
  protected classifyErrorInternal(error: unknown): StandardAuthError {
    const cacheIdentity = ClassifyMJCacheIdentityError(error);
    if (cacheIdentity !== null) return cacheIdentity;
    if (error instanceof oauth.ResponseBodyError || error instanceof oauth.AuthorizationResponseError) {
      const code = error.error;
      const message = error.error_description || error.message;
      if (code === 'invalid_grant') {
        return { type: AuthErrorType.TOKEN_EXPIRED, message, originalError: error, userMessage: 'Your session has expired. Please log in again.' };
      }
      if (code === 'access_denied') {
        return { type: AuthErrorType.USER_CANCELLED, message, originalError: error, userMessage: 'Login was cancelled.' };
      }
      if (code === 'login_required') {
        return { type: AuthErrorType.NO_ACTIVE_SESSION, message, originalError: error, userMessage: 'You need to log in to continue.' };
      }
      if (code === 'consent_required' || code === 'interaction_required') {
        return {
          type: AuthErrorType.INTERACTION_REQUIRED,
          message,
          originalError: error,
          userMessage: 'Additional authentication is required. Please log in again.',
        };
      }
      return { type: AuthErrorType.CONFIGURATION_ERROR, message, originalError: error, userMessage: 'Sign-in failed. Please try again.' };
    }
    if (error instanceof oauth.WWWAuthenticateChallengeError) {
      return {
        type: AuthErrorType.TOKEN_EXPIRED,
        message: error.message,
        originalError: error,
        userMessage: 'Your session has expired. Please log in again.',
      };
    }
    if (error instanceof TypeError) {
      return {
        type: AuthErrorType.NETWORK_ERROR,
        message: error.message,
        originalError: error,
        userMessage: 'Network error. Please check your connection and try again.',
      };
    }
    const message = error instanceof Error ? error.message : 'Unknown error';
    return { type: AuthErrorType.UNKNOWN_ERROR, message, originalError: error, userMessage: 'An unexpected error occurred. Please try again.' };
  }

  /**
   * ForgeRock may include `picture` in ID token claims, like Auth0. No separate userinfo call is
   * made — a dedicated `/userinfo` round trip isn't needed for a claim already on the token this
   * provider already holds.
   */
  protected async getProfilePictureUrlInternal(): Promise<string | null> {
    if (!this.idToken) {
      return null;
    }
    const claims = ExtractForgeRockClaims(DecodeUnverifiedJwtPayload(this.idToken));
    return claims.picture ?? null;
  }

  /**
   * The fallback leg, not the common one: `refreshTokenInternal()` above normally renews the ID
   * token silently with AGU's refresh token (verified live 2026-09-23). This runs only when no
   * usable refresh token exists — absent, past its 7-day expiry, or revoked at the IdP — where the
   * only recovery is a fresh interactive login. `MJAuthBase.refreshToken()`'s own docstring
   * (node_modules/@memberjunction/ng-auth-services/dist/lib/mjexplorer-auth-base.service.js:176-178,
   * 207-209) says this hook "may redirect and never return" — so once the authorize navigation is
   * issued, this deliberately never settles.
   *
   * `window.location.assign()` returns immediately and the document stays alive until the
   * navigation commits. If this resolved, the base would retry `refreshTokenInternal()`, fail
   * again, and throw; `notifyAuthenticationError` → the workspace initializer's
   * `handleSessionExpired` (ng-workspace-initializer/dist/lib/services/workspace-initializer.service.js:245-262)
   * then schedules `logout()` on a 3 000 ms timer. On a slow IdP response that timer fires first
   * and `logoutInternal()` clears the session (including the return-to) and navigates to the
   * `end_session_endpoint`, cancelling the in-flight authorize navigation: the user is signed out
   * of AGU and loses the page they were on. Never settling keeps the redirect the only outcome.
   * A discovery failure inside `loginInternal()` still rejects, which keeps today's behaviour
   * (base throws → overlay → forced logout) for the case where no navigation was issued.
   */
  protected async handleSessionExpiryInternal(): Promise<void> {
    await this.loginInternal();
    return new Promise<never>(() => undefined);
  }

  // ============================================================================
  // CONFIGURATION
  // ============================================================================

  override getRequiredConfig(): string[] {
    return ['clientId'];
  }

  override validateConfig(_config: Record<string, unknown>): boolean {
    // Angular-side configuration is supplied via angularProviderFactory/FORGEROCK_CONFIG,
    // not the generic AngularAuthProviderConfig object AngularAuthProviderFactory.createProvider
    // validates — mirrors the Auth0 provider's validateConfig (always true; real validation is
    // IsForgeRockConfigured, checked at each use site: initialize/loginInternal/handleCallback).
    return true;
  }

  // ============================================================================
  // HELPERS
  // ============================================================================

  /**
   * Is the current URL *this provider's* OAuth callback? ForgeRock only ever redirects back to the
   * registered `FORGEROCK_REDIRECT_URI` (the bare origin on stage/prod, so pathname `/`), so both
   * the path and the `code=`/`error=` params must match before this provider claims the URL.
   *
   * Without the path check, this gate also swallows MJExplorer's own `/oauth/callback` route
   * (ng-explorer-core's guard-free MCP OAuth callback, which reads `code`/`state` off the URL after
   * the session is restored): the exchange would fail for want of PKCE keys, the URL would be
   * stripped, and the user would land unauthenticated with the code gone. Same for any future deep
   * link that happens to use a `code=`/`error=` query key.
   *
   * `window.location.origin` is passed as the base so a relative FORGEROCK_REDIRECT_URI still
   * resolves; for the absolute URI every real deployment sets, the base is ignored and the pathname
   * is the redirect URI's own. That base does NOT make the construction infallible — an opaque
   * document origin serialises to the string `"null"`, which is not a valid base URL and makes
   * `new URL()` throw, whatever the redirect URI is. A throw here would escape `initialize()` and
   * blank the app (spec H2/P1: a rejected APP_INITIALIZER aborts the Angular bootstrap), so it is
   * caught: "not our callback" is the safe answer — the URL is left untouched and the user gets the
   * login screen instead of a blank page.
   */
  private isForgeRockCallbackUrl(): boolean {
    let redirectPath: string;
    try {
      redirectPath = new URL(this.forgerockConfig.FORGEROCK_REDIRECT_URI, window.location.origin).pathname;
    } catch (error) {
      console.error('[ForgeRock] FORGEROCK_REDIRECT_URI could not be parsed; not treating this URL as the callback.', error);
      return false;
    }
    return window.location.pathname === redirectPath && UrlHasAuthCallbackParams(window.location.search);
  }

  private async ensureDiscovery(): Promise<oauth.AuthorizationServer> {
    if (this.authServer) {
      return this.authServer;
    }
    const issuer = new URL(this.forgerockConfig.FORGEROCK_ISSUER);
    const response = await oauth.discoveryRequest(issuer, { algorithm: 'oidc', ...this.insecureRequestOptions() });
    this.authServer = await oauth.processDiscoveryResponse(issuer, response);
    return this.authServer;
  }

  /**
   * `oauth4webapi@3.8.6` refuses any non-HTTPS request (`OAUTH_HTTP_REQUEST_FORBIDDEN`) unless
   * `[oauth.allowInsecureRequests]: true` is passed on every call that hits the network. Every
   * `HttpRequestOptions`-derived options type (`DiscoveryRequestOptions`,
   * `TokenEndpointRequestOptions`, ...) carries that same optional symbol-keyed field, so one
   * object can be spread into all three call sites below. Gated on `IsLocalInsecureIssuer` — this
   * is the *only* place `http://` is ever allowed, and only for `localhost`/`127.0.0.1` (the
   * `tools/forgerock-mock/` rehearsal issuer). A stage/prod ForgeRock issuer is always `https://`,
   * so this evaluates to `{}` (empty options) in every real deployment.
   */
  private insecureRequestOptions(): { [oauth.allowInsecureRequests]?: boolean } {
    if (IsLocalInsecureIssuer(this.forgerockConfig.FORGEROCK_ISSUER)) {
      return { [oauth.allowInsecureRequests]: true };
    }
    return {};
  }

  private async generatePkceAttempt(): Promise<PkceAttempt> {
    const codeVerifier = oauth.generateRandomCodeVerifier();
    const codeChallenge = await oauth.calculatePKCECodeChallenge(codeVerifier);
    return {
      codeVerifier,
      codeChallenge,
      state: oauth.generateRandomState(),
      nonce: oauth.generateRandomNonce(),
    };
  }

  private persistTokens(idToken: string, refreshToken: string | null, expiresAtMs: number): void {
    this.idToken = idToken;
    this.refreshTokenValue = refreshToken;
    this.tokenExpiresAt = expiresAtMs;
    sessionStorage.setItem(ID_TOKEN_KEY, idToken);
    sessionStorage.setItem(EXPIRES_AT_KEY, String(expiresAtMs));
    // AGU's tenant returns a refresh token on the code grant even without `offline_access` in
    // FORGEROCK_SCOPES (verified live 2026-09-23 — see refreshTokenInternal's doc comment), so this
    // key is populated in normal operation and is what makes the silent 60-minute refresh work.
    // The `else` still matters: a tenant/config that returns none must not leave a stale one behind.
    if (refreshToken) {
      sessionStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
    } else {
      sessionStorage.removeItem(REFRESH_TOKEN_KEY);
    }
  }

  /** @returns `true` when the cached token decoded and user info was published, `false` otherwise. */
  private restoreUserInfoFromIdToken(idToken: string): boolean {
    try {
      const claims = ExtractForgeRockClaims(DecodeUnverifiedJwtPayload(idToken));
      this.updateUserInfo(MapForgeRockClaimsToStandardUserInfo(claims));
      return true;
    } catch (error) {
      console.error('[ForgeRock] Error restoring user info from cached ID token:', error);
      return false;
    }
  }

  private clearSession(): void {
    this.idToken = null;
    this.refreshTokenValue = null;
    this.tokenExpiresAt = 0;
    sessionStorage.removeItem(ID_TOKEN_KEY);
    sessionStorage.removeItem(REFRESH_TOKEN_KEY);
    sessionStorage.removeItem(EXPIRES_AT_KEY);
    sessionStorage.removeItem(RETURN_TO_KEY);
  }
}
